




import { Component, Mixins, Prop } from "vue-property-decorator";
import {
  ExternalScriptManager,
  ExternalScriptParameter,
} from "../../../../domain/document";
import {
  IItem,
  IExternalScriptExecutable,
  IExternalDataApplicable,
  instanceOfIExternalScriptExecutable,
} from "../../../../domain/document/items";
import {
  ExecuteRequest,
  ExternalScriptExecuteResult,
} from "../../../../domain/localhost/items";
import { Dictionary } from "../../../../domain/system";
import { ModelEditableMixin } from "../../../system/mixins";
import { EditingDocument } from "..";
import { ApplicationDialogsAccessor } from "../CommonComponentsAccessor";
import { ExtraPageEditor } from "../editors";
import { DocumentStructureEditableMixin } from "../mixins";

@Component
export default class ExternalScriptAdapter extends Mixins(ModelEditableMixin, DocumentStructureEditableMixin) {
  editingSource: EditingDocument;

  @Prop()
  dialogs: ApplicationDialogsAccessor;

  @Prop()
  extraPageEditor: ExtraPageEditor;

  async executeExternalData(source: IExternalScriptExecutable & IExternalDataApplicable): Promise<void> {
    const result = await this.execute(source, "externalData");
    if (!result) { return; }
    this.applyExternalDataResultValues(result, this.itemSetId, this.items, source.privateId);
    this.addExternalDataOverflowedTextPages(source);
  }

  async executeExternalProcess(source: IExternalScriptExecutable): Promise<void> {
    const result = await this.execute(source, "externalProcess");
    if (!result) { return; }
    this.addExternalProcessOverflowedTextPages(this.applyExternalProcessResultValues(result, this.itemSetId, this.items, this.externalScriptParameter));
  }

  private applyExternalDataResultValues(result: ExternalScriptExecuteResult, itemSetId: number, items: IItem[], sourcePrivateId: number): void {
    this.writePropertyWhenValid((s: EditingDocument) => s.externalScriptRunner.applyExternalDataResultValues(result, itemSetId, sourcePrivateId));
    // editingSourceの更新が反映されてから実行.
    this.$nextTick(() => {
      this.executeStackedScripts();
    });
  }

  private applyExternalProcessResultValues(result: ExternalScriptExecuteResult, itemSetId: number, items: IItem[], param: ExternalScriptParameter): IItem[] {
    let updatedItems: IItem[] = [];
    this.writePropertyWhenValid(s => {
       updatedItems = s.externalScriptRunner.applyExternalProcessResultValues(result, itemSetId, param);
    });
    // editingSourceの更新が反映されてから実行.
    this.$nextTick(() => {
      this.executeStackedScripts();
    });
    return updatedItems;
  }

  private async addExternalDataOverflowedTextPages(source: IExternalScriptExecutable): Promise<void> {
    const index = this.currentSectionIndex;
    for (const item of this.items) {
      if (!this.shouldAddOverflowedTextPage(item, source.name)) {
        continue;
      }
      await this.extraPageEditor.addOverflowedTextPage(item);
      await this.$nextTick(); // 続紙ページの反映を待つ
      this.currentSectionIndex = index;
      await this.$nextTick(); // 戻したページの反映を待つ
    }
  }

  private shouldAddOverflowedTextPage(source: IItem, name: string): boolean {
    return instanceOfIExternalScriptExecutable(source)
        && (source.name === name)
        && this.extraPageEditor.isAddibleOverflowedTextPage(source);
  }

  private async addExternalProcessOverflowedTextPages(updatedItems: IItem[]): Promise<void> {
    const index = this.currentSectionIndex;
    for (const item of updatedItems) {
      if (!await this.extraPageEditor.addOverflowedTextPage(item)) { continue; }
      await this.$nextTick(); // 続紙ページの反映を待つ
      this.currentSectionIndex = index;
      await this.$nextTick(); // 戻したページの反映を待つ
    }
  }

  private async execute(item: IExternalScriptExecutable, action: string): Promise<ExternalScriptExecuteResult> {
    const result = await this.launch(item, action);
    if (!result) {
      this.$warn("キャンセル", "外部スクリプト処理がキャンセルされました。");
      return null;
    }
    if (!result.isComplete) {
      throw new Error("外部スクリプト処理でエラーが発生しました。");
    }
    if (!result.name) {
      // TODO:fix
      result.name = item.name;
    }
    return result;
  }

  private async launch(item: IExternalScriptExecutable, action: string): Promise<ExternalScriptExecuteResult> {
    return this.dialogs.launcher.show(
      null,
      this.createParameters(action),
      this.createAdditionalParameter(item));
  }

  private createParameters(action: string): Dictionary<string, string> {
    const parameters = new Dictionary<string, string>();
    parameters.set("action", action);
    return parameters;
  }

  private createAdditionalParameter(item: IExternalScriptExecutable): ExecuteRequest {
    const sections = this.sections.filter(s => s.itemSetId === this.itemSetId);
    const manager = new ExternalScriptManager(this.itemSet, sections);
    return manager.getAdditionalParameter(item, this.externalScriptParameter);
  }

  private get externalScriptParameter(): ExternalScriptParameter {
    return new ExternalScriptParameter(
      this.editingSource.attributes.patient,
      this.$store.getters.user,
      this.editingSource.attributes.department,
      this.editingSource.attributes.orderNo,
      this.editingSource.attributes.format.originalId,
      this.editingSource.attributes.hisInfoGetterParameter);
  }
}
